import { ReactNode } from "react";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatCurrency, getChargeRows } from "../../utils";
import OutcomeCard, { OutcomeRows, ReferenceBlock } from "./OutcomeCard";

const SuccessCard = ({
  totalAmount,
  instrumentFee = "",
  registrationFee = "",
  totalFees = "",
  paymentReference,
  currency,
  paidWith,
  paidFor,
  customer,
  actions,
}: {
  totalAmount: string;
  instrumentFee?: string;
  registrationFee?: string;
  totalFees?: string;
  // The reference the payer quotes to the school or to support. Absent
  // whenever the payment has none that qualifies, and absent means nothing
  // renders -- no label, no empty row.
  paymentReference?: string;
  // The unit the pay response named, for the amounts on the receipt.
  currency?: string;
  // "Method · grouped number" the payer chose. Absent on a reload, where
  // router state no longer carries it.
  paidWith?: string;
  // What the payment was for: the fee, else the service.
  paidFor?: string;
  // The customer a searched deposit was made for, when the journey held one.
  customer?: string;
  actions?: ReactNode;
}) => {
  // The hook, matching errorCard: the card subscribes to locale changes
  // itself instead of leaning on a parent that happens to re-render.
  const { t } = useTranslation();

  // Same breakdown the payer was shown before paying, from one owner, so
  // the receipt cannot contradict the confirm screen. Absent on a reload,
  // where the session no longer holds the quote.
  const chargeRows =
    Number(totalFees) > 0
      ? getChargeRows(instrumentFee, registrationFee, totalFees)
      : [];

  return (
    <OutcomeCard
      tone="success"
      title={t("stepper_3.successful_payment")}
      total={
        Number(totalAmount) > 0
          ? formatCurrency(totalAmount, undefined, currency)
          : undefined
      }
      actions={actions}
    >
      <Typography sx={{ fontSize: 15, color: "text.secondary" }}>
        {t("stepper_3.payment_success_message")}
      </Typography>
      <OutcomeRows
        label={t("outcome.summary")}
        rows={[
          { term: t("outcome.paid_for"), value: paidFor },
          { term: t("outcome.customer"), value: customer },
          { term: t("review.pay_with"), value: paidWith },
          ...chargeRows.map(row => ({
            term: t(row.titleKey),
            value: formatCurrency(row.value, undefined, currency),
          })),
        ]}
      />
      {paymentReference && (
        <ReferenceBlock
          label={t("payment_reference.label")}
          value={paymentReference}
          copyLabel={t("copy.payment_reference")}
          help={t("payment_reference.help")}
        />
      )}
    </OutcomeCard>
  );
};

export default SuccessCard;
